/**
 * La primera pantalla de TORA. Literalmente: la que se ve una sola vez, cuando
 * la base todavia no tiene ningun usuario.
 *
 * POR QUE NO ES UN FORMULARIO ADMINISTRATIVO
 * ------------------------------------------
 * Del otro lado no hay un administrador de sistemas: hay alguien que vende
 * iPhones y que acaba de abrir una herramienta que le van a pedir que use todos
 * los dias durante años. Lo que pasa en estos treinta segundos define si TORA le
 * parece suyo o le parece un tramite.
 *
 * De ahi las tres decisiones de esta pantalla:
 *   · Se le habla de vos, con el nombre del negocio, no de "el sistema".
 *   · Se explica en una linea QUE es lo que esta creando y por que es especial:
 *     es la cuenta de administrador, la unica que puede crear las demas.
 *   · La contraseña se puede ver mientras se escribe. Es la unica vez que se
 *     inventa una y no hay "olvide mi contraseña" del otro lado: un error de
 *     tipeo aca es quedarse afuera de su propio sistema.
 *
 * `POST /sesion/instalar` funciona SOLO con la tabla de usuarios vacia; despues
 * contesta 409 para siempre. No queda ninguna credencial por defecto dando
 * vueltas.
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Check } from "lucide-react";
import { useEffect, useRef, useState, type FormEvent } from "react";

import { LogoManzana } from "@/components/Logo";
import { PageHeader } from "@/components/layout/PageHeader";
import { Aviso } from "@/components/ui/aviso";
import { Button } from "@/components/ui/button";
import { Campo } from "@/components/ui/campo";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { InputPassword } from "@/components/ui/input-password";
import { ErrorApi, mensajeDeError } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";

export const Route = createFileRoute("/instalar")({ component: Instalar });

/** El mismo minimo que exige el backend. Se avisa antes de mandar, no despues. */
const MINIMO_PASSWORD = 8;

function Instalar(): JSX.Element {
  const { cargando, instalado, instalar, operando } = useAuth();
  const navegar = useNavigate();

  const [nombre, setNombre] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<ErrorApi | Error | null>(null);
  const campoNombre = useRef<HTMLInputElement>(null);

  // Si alguien llega aca con el sistema ya instalado, el backend contestaria 409.
  // Mejor mandarlo al login antes de que escriba nada.
  useEffect(() => {
    if (!cargando && instalado) void navegar({ to: "/login", replace: true });
  }, [cargando, instalado, navegar]);

  useEffect(() => {
    campoNombre.current?.focus();
  }, []);

  const passwordCorta = password.length > 0 && password.length < MINIMO_PASSWORD;

  async function enviar(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    try {
      await instalar({ nombre: nombre.trim(), email: email.trim(), password });
      // El backend ya dejo la cookie puesta: se entra derecho, sin pedirle que
      // vuelva a escribir la contraseña que acaba de inventar.
      await navegar({ to: "/", replace: true });
    } catch (falla) {
      setError(falla instanceof Error ? falla : new Error(mensajeDeError(falla)));
    }
  }

  const detalles = error instanceof ErrorApi ? error.detalles : {};

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-background">
      <div className="mx-auto flex min-h-full w-full max-w-md flex-col justify-center gap-6 p-4 py-8">
        <div className="space-y-4">
          <LogoManzana className="size-10 text-primary" />
          <PageHeader
            titulo="Bienvenido a MANZANA MORDIDA"
            linea={false}
            descripcion="De acá en adelante, MANZANA MORDIDA se maneja desde acá: los precios en pesos y en dólares, el stock, las cuentas de los clientes, la caja y el taller. Todavía no hay nadie cargado, así que empezamos por vos."
          />
        </div>

        <Card padding="normal">
          <p className="mb-4 text-sm">
            Estás creando tu <span className="font-medium">cuenta de administrador</span>: la
            primera del sistema y la única que puede crear las demás.
          </p>

          <form onSubmit={(e) => void enviar(e)} noValidate className="space-y-4">
            <Campo label="Tu nombre" error={detalles.nombre?.join(" ")}>
              <Input
                ref={campoNombre}
                name="nombre"
                autoComplete="name"
                placeholder="Como querés que te llame el sistema"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                disabled={operando}
              />
            </Campo>

            <Campo
              label="Tu mail"
              error={detalles.email?.join(" ")}
              ayuda={detalles.email ? undefined : "Es con lo que vas a entrar todos los días."}
            >
              <Input
                type="email"
                name="email"
                autoComplete="username"
                inputMode="email"
                autoCapitalize="none"
                spellCheck={false}
                placeholder="vos@manzanamordida.ar"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={operando}
              />
            </Campo>

            <Campo
              label="Una contraseña"
              error={detalles.password?.join(" ")}
              ayuda={
                detalles.password ? undefined : password.length >= MINIMO_PASSWORD ? (
                  <span className="inline-flex items-center gap-1 text-success [&_svg]:size-3.5">
                    <Check aria-hidden="true" />
                    Listo. Anotala en algún lado seguro.
                  </span>
                ) : (
                  <span className={passwordCorta ? "text-destructive" : undefined}>
                    Mínimo {MINIMO_PASSWORD} caracteres. Podés verla con el ojito mientras la
                    escribís.
                  </span>
                )
              }
            >
              <InputPassword
                name="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                aria-invalid={passwordCorta || undefined}
                disabled={operando}
              />
            </Campo>

            {error ? (
              <Aviso tono="destructive" aria-live="assertive">
                {mensajeDeError(error)}
              </Aviso>
            ) : null}

            <Button
              type="submit"
              size="lg"
              className="w-full"
              cargando={operando}
              disabled={password.length < MINIMO_PASSWORD}
            >
              Crear mi cuenta y empezar
            </Button>
          </form>
        </Card>

        <p className="text-center text-xs text-muted-foreground">
          Después vas a poder crear cuentas para quien atienda el mostrador.
        </p>
      </div>
    </div>
  );
}
